import React, { useMemo, useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal, Image, Platform } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { LoadingSpinner } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { 
  useGetTaskDetailsQuery,
  useArchiveTaskMutation,
  useDuplicateTaskMutation,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from '@/store/api/admin/tasksApi';
import { ITaskItemAttributes, TASK_PRIORITY, TASK_STATUS_CONFIG } from '@/types/admin/tasks';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { WebConfirmModal } from '@/components/common/modals';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { DetailsHeader } from './details/DetailsHeader';
import { DetailsSection } from './details/DetailsSection';
import { SubtasksList } from './details/SubtasksList';
import { AttachmentsGrid } from './details/AttachmentsGrid';
import { getStatusColorHex } from '@/utils/admin/tasks';

interface DesktopTaskDetailsPanelProps {
  taskId: number | null;
  visible: boolean;
  onClose: () => void;
  onEdit?: (taskId: number) => void;
}

export const DesktopTaskDetailsPanel: React.FC<DesktopTaskDetailsPanelProps> = ({
  taskId,
  visible,
  onClose,
  onEdit,
}) => {
  const enabled = !!taskId && visible;
  const { data: task, isFetching } = useGetTaskDetailsQuery(
    { taskId: taskId || 0 },
    { skip: !enabled }
  );

  const [archiveTask, { isLoading: isArchiving }] = useArchiveTaskMutation();
  const [duplicateTask, { isLoading: isDuplicating }] = useDuplicateTaskMutation();
  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();

  const [filePreview, setFilePreview] = useState<{ visible: boolean; url?: string; name?: string } | null>(null);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [statusAnchor, setStatusAnchor] = useState<AnchorRect | null>(null);
  const statusButtonRef = useRef<View | null>(null);
  const statusButtonId = useMemo(() => `task-status-btn-${task?.id ?? 'unknown'}`,[task?.id]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  const priorityConfig = useMemo(() => {
    if (!task) return TASK_PRIORITY[0];
    return TASK_PRIORITY.find(p => p.value === task.priority) || TASK_PRIORITY[0];
  }, [task]);

  const getStatusColorHexLocal = (statusValue: ITaskItemAttributes['taskStatus']) => getStatusColorHex(statusValue as any);

  const openStatusPicker = useCallback(() => {
    try {
      if (Platform.OS === 'web') {
        const el: any = (globalThis as any)?.document?.querySelector?.(`[data-testid="${statusButtonId}"]`);
        if (el && el.getBoundingClientRect) {
          const r = el.getBoundingClientRect();
          setStatusAnchor({ x: r.left, y: r.top, w: r.width, h: r.height });
          setShowStatusPicker((prev) => !prev);
          return;
        }
      }
      if (statusButtonRef.current) {
        const RN: any = require('react-native');
        const node = RN.findNodeHandle(statusButtonRef.current);
        if (node && RN.UIManager && RN.UIManager.measureInWindow) {
          RN.UIManager.measureInWindow(node, (x: number, y: number, width: number, height: number) => {
            setStatusAnchor({ x, y, w: width, h: height });
            setShowStatusPicker((prev) => !prev);
          });
          return;
        }
      }
      setShowStatusPicker((prev) => !prev);
    } catch {
      setShowStatusPicker((prev) => !prev);
    }
  }, [statusButtonId]);

  const formatDueDateLong = (iso?: string | null) => {
    if (!iso) return '--';
    try {
      const d = new Date(iso as any);
      // Prefer long-form like admin-web
      // Some RN runtimes may not support full locale options; fall back gracefully
      const formatted = (d as any).toLocaleDateString
        ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' } as any)
        : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      return formatted;
    } catch {
      return '--';
    }
  };

  const statusLabel = useMemo(() => {
    if (!task) return TASK_STATUS_CONFIG.pending.label;
    return ((TASK_STATUS_CONFIG as any)[task.taskStatus]?.label as string) || task.taskStatus;
  }, [task]);

  const handleArchiveToggle = async (t: ITaskItemAttributes) => {
    await archiveTask({ taskId: t.id, isArchived: !t.isArchived, archivedAt: !t.isArchived ? new Date().toISOString() : null });
    onClose();
  };

  const handleDuplicate = async (t: ITaskItemAttributes) => {
    // selectedPropertyId is provided server-side by existing endpoint behavior; duplication uses original task payload
    await duplicateTask({ taskId: t.id, originalTask: t, selectedPropertyId: t.propertyId });
    onClose();
  };

  const handleDelete = async (t: ITaskItemAttributes) => {
    await deleteTask({ taskId: t.id });
    onClose();
  };

  const handleChangeStatus = async (nextStatus: ITaskItemAttributes['taskStatus']) => {
    if (!task) return;
    // Optimistic UI: update local UI immediately while mutation runs
    const original = { ...task } as ITaskItemAttributes;
    const optimistic = { ...task, taskStatus: nextStatus } as ITaskItemAttributes;
    try {
      // Close picker immediately for responsiveness
      setShowStatusPicker(false);
      // Fire mutation; details query will revalidate via tags
      await updateTask({
        taskId: task.id,
        taskForm: optimistic as any,
        selectedPropertyId: task.propertyId,
      }).unwrap();
    } catch (e) {
      // No explicit rollback needed because server response re-syncs; if desired, we could set local state
      // setLocalTask(original) // not used; query will refetch
    }
  };

  const toggleSubtask = useCallback(async (index: number) => {
    if (!task || task.isArchived) return;
    // Optimistic UI
    const nextSubtasks = [...(task.subtasks || [])];
    const st = { ...nextSubtasks[index] } as any;
    st.completedAt = st.completedAt ? null : new Date().toISOString();
    nextSubtasks[index] = st;
    try {
      await updateTask({
        taskId: task.id,
        taskForm: { ...task, subtasks: nextSubtasks } as any,
        selectedPropertyId: task.propertyId,
      }).unwrap();
    } catch (e) {}
  }, [task, updateTask]);

  const onPressFile = (file: any) => {
    const name = file.label || file.name;
    const url = file.fileUrl;
    setFilePreview({ visible: true, url, name });
  };

  return (
    <DesktopSidePanel visible={visible} onClose={onClose} width={520}>
      <View style={styles.container}>
        {task?.isArchived && (
          <View style={styles.archivedBanner}>
            <SVG_COMPONENTS.WARNING width={14} height={14} color={ADMIN_COLORS.TEXT_SECONDARY} />
            <Text style={styles.archivedBannerText}>Unarchive task to edit details</Text>
          </View>
        )}
        <DetailsHeader
          statusLabel={statusLabel}
          statusValue={(task?.taskStatus || 'pending') as any}
          isArchived={!!task?.isArchived}
          onOpenStatusPicker={openStatusPicker}
          onEdit={task ? () => onEdit?.(task.id) : undefined}
          onClose={onClose}
          statusButtonRef={statusButtonRef as any}
          statusButtonId={statusButtonId}
          statusColorHex={getStatusColorHexLocal((task?.taskStatus || 'pending') as any)}
        />
        <AnchoredDropdown
          isOpen={!!task && showStatusPicker && !task.isArchived}
          anchor={statusAnchor}
          width={220}
          maxHeight={320}
          onRequestClose={() => setShowStatusPicker(false)}
        >
          <View>
            {Object.values(TASK_STATUS_CONFIG)
              .filter((s) => s.value !== TASK_STATUS_CONFIG.incoming.value)
              .map((s) => (
                <TouchableOpacity
                  key={s.value}
                  style={styles.dropdownItem}
                  onPress={() => handleChangeStatus(s.value as any)}
                >
                  {s.value === TASK_STATUS_CONFIG.cancelled.value ? (
                    <SVG_COMPONENTS.X_CIRCLE width={12} height={12} color={'#F87171'} />
                  ) : (
                    <View style={[styles.statusDot, { marginRight: 8, backgroundColor: getStatusColorHexLocal(s.value as any) }]} />
                  )}
                  <Text style={styles.dropdownText}>{s.label}</Text>
                </TouchableOpacity>
              ))}
          </View>
        </AnchoredDropdown>
        <ScrollView style={styles.body} contentContainerStyle={{ paddingBottom: 24 }}>
          {!!task && (
            <View>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{task.title}</Text>
                {!!task.description && (
                  <>
                    <Text style={styles.sectionLabel}>INTERNAL NOTES</Text>
                    <Text style={styles.description} numberOfLines={showFullDescription ? undefined : 6}>
                      {task.description}
                    </Text>
                    {task.description.length > 180 && (
                      <TouchableOpacity
                        onPress={() => setShowFullDescription(prev => !prev)}
                        style={{ alignSelf: 'center', marginTop: 8 }}
                      >
                        <Text style={{ color: ADMIN_COLORS.TEXT_SECONDARY, fontSize: 12 }}>
                          {showFullDescription ? 'Show Less' : 'Show More'}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </>
                )}
              </View>

              <DetailsSection label="Assignees">
                {(!task.assignees || task.assignees.length === 0) ? (
                  <Text style={styles.valueText}>No assignees</Text>
                ) : (
                  <DesktopOverflowText
                    text={`${(task.assignees[0].firstName || '')} ${(task.assignees[0].lastName || '')}`.trim() + (task.assignees.length > 1 ? ` +${task.assignees.length - 1}` : '')}
                    title={'Assigned to:'}
                    tooltipText={task.assignees.map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim()).join('\n')}
                    numberOfLines={1}
                  />
                )}
              </DetailsSection>

              <DetailsSection label="Priority">
                <View style={[styles.pill, { backgroundColor: 'rgba(255,255,255,0.06)', borderColor: ADMIN_COLORS.BORDER }]}> 
                  <Text style={styles.pillText}>{priorityConfig.label}</Text>
                </View>
              </DetailsSection>

              <DetailsSection label="Due Date">
                <Text style={styles.valueText}>{formatDueDateLong(task.dueDate as any)}</Text>
              </DetailsSection>

              {false && (
                <View style={styles.section}>
                  <Text style={styles.sectionLabel}>Category</Text>
                  <Text style={styles.valueText}>{''}</Text>
                </View>
              )}

              <DetailsSection label="Location">
                <Text style={styles.valueText}>{task.location || '--'}</Text>
              </DetailsSection>

              <DetailsSection label="Subtasks">
                <View style={styles.sectionHeaderRow}>
                  <View />
                  <Text style={styles.sectionCount}>{task.subtasks?.length || 0}</Text>
                </View>
                <SubtasksList
                  subtasks={task.subtasks as any}
                  isArchived={!!task.isArchived}
                  onToggle={(idx) => toggleSubtask(idx)}
                />
              </DetailsSection>

              <DetailsSection label="Attachments" containerStyle={{ marginBottom: 80 }}>
                <AttachmentsGrid files={task.files as any} onPressFile={onPressFile} />
              </DetailsSection>
            </View>
          )}
        </ScrollView>

        {/* Loading state when opening a task with no cached details yet */}
        {!task && isFetching && (
          <View style={styles.initialLoadingContainer}>
            <LoadingSpinner message="Loading task details..." />
          </View>
        )}

        <Modal visible={!!filePreview?.visible} transparent animationType="fade" onRequestClose={() => setFilePreview(null)}>
          <View style={styles.previewOverlay}>
            <View style={styles.previewContent}>
              <View style={styles.previewHeader}>
                <Text style={styles.previewTitle} numberOfLines={1}>{filePreview?.name || 'Attachment'}</Text>
                <TouchableOpacity onPress={() => setFilePreview(null)}>
                  <SVG_COMPONENTS.CANCEL_X width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
                </TouchableOpacity>
              </View>
              {!!filePreview?.url && (
                <Image source={{ uri: filePreview.url }} resizeMode="contain" style={styles.previewImage} />
              )}
            </View>
          </View>
        </Modal>

        <View style={styles.footer}>
          <View style={{ flex: 1, flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              style={[styles.iconButton]}
              onPress={() => task && handleDuplicate(task)}
              disabled={!task || isDuplicating}
            >
              <SVG_COMPONENTS.COPY width={18} height={18} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.iconButton]}
              onPress={() => task && handleArchiveToggle(task)}
              disabled={!task || isArchiving}
            >
              {task?.isArchived ? (
                <SVG_COMPONENTS.ARCHIVE width={18} height={18} />
              ) : (
                <SVG_COMPONENTS.FOLDER width={18} height={18} />
              )}
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={[styles.deleteButton]}
            onPress={async () => {
              if (!task || isDeleting) return;
              if (Platform.OS === 'web') {
                setConfirmDeleteVisible(true);
              } else {
                const isArchived = !!task.isArchived;
                const title = 'Delete Task';
                const message = isArchived
                  ? 'Delete this archived task? This action is permanent.'
                  : 'Delete this task? This action is permanent.\nRecommended to archive instead.';
                
                // Use cross-platform confirmation (simplified to 2 buttons)
                const confirmed = await showConfirm(
                  title,
                  message,
                  'Delete',
                  'Cancel'
                );
                
                if (confirmed) {
                  handleDelete(task);
                }
              }
            }}
            disabled={!task || isDeleting}
          >
            <SVG_COMPONENTS.TRASH width={18} height={18} color={'#fff'} />
          </TouchableOpacity>
        </View>
        <WebConfirmModal
          visible={Platform.OS === 'web' && confirmDeleteVisible}
          title={'Delete Task'}
          message={task?.isArchived
            ? 'Delete this archived task? This action is permanent.'
            : 'Delete this task? This action is permanent.\nRecommended to archive instead.'}
          confirmText={'Delete'}
          cancelText={'Cancel'}
          onConfirm={() => { if (task) handleDelete(task); setConfirmDeleteVisible(false); }}
          onCancel={() => setConfirmDeleteVisible(false)}
        />

        {/* Refreshing overlay to prevent stale content flash when switching tasks */}
        {task && isFetching && (
          <View style={styles.loadingOverlay} pointerEvents="none">
            <LoadingSpinner message="Refreshing..." />
          </View>
        )}
      </View>
    </DesktopSidePanel>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderLeftWidth: 1,
    borderLeftColor: ADMIN_COLORS.BORDER,
  },
  archivedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  archivedBannerText: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  statusBadgeCancelled: {
    opacity: 0.9,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.TEXT_SECONDARY,
  },
  statusText: {
    color: ADMIN_COLORS.TEXT.SECONDARY,
    fontSize: 12,
    textTransform: 'capitalize',
  },
  dropdownPanel: {
    position: 'absolute',
    top: 40,
    left: 0,
    right: 'auto',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    paddingVertical: 4,
    minWidth: 180,
    zIndex: 20,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 8,
  },
  dropdownText: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 12,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  secondaryButtonText: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 12,
    fontWeight: '500',
  },
  body: {
    flex: 1,
  },
  initialLoadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  titleBlock: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  title: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  sectionLabel: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 12,
    marginBottom: 6,
  },
  description: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 14,
    lineHeight: 20,
  },
  valueText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 14,
  },
  mutedText: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
    fontSize: 12,
  },
  pill: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    alignSelf: 'flex-start',
  },
  pillText: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 12,
    fontWeight: '600',
  },
  subtaskList: {
    gap: 8,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionCount: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 12,
  },
  progressTrack: {
    height: 6,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    overflow: 'hidden',
    marginTop: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  subtaskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  subtaskText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 14,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 3,
  },
  checkboxChecked: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  subtaskCompleted: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textDecorationLine: 'line-through',
  },
  filesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  fileItem: {
    width: '48%',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    padding: 10,
    gap: 8,
  },
  fileThumb: {
    width: '100%',
    height: 120,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  fileLabel: {
    color: ADMIN_COLORS.TEXT_SECONDARY,
    fontSize: 11,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  iconButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  deleteButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 18,
    backgroundColor: ADMIN_COLORS.ERROR,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.0)',
  },
  previewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  previewContent: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    width: '90%',
    maxWidth: 700,
    maxHeight: '80%',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  previewTitle: {
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontSize: 14,
    flex: 1,
    marginRight: 12,
  },
  previewImage: {
    width: '100%',
    height: 420,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
});


